import { TeoCore } from '@/teoCore/TeoCore';

const LoggerLevel = {
  all: 0,
  trace: 10,
  dev: 15,
  debug: 20,
  log: 30,
  info: 40,
  warn: 50,
  error: 60,
  none: 999,
} as const;

const LOG_METHODS = ['trace', 'debug', 'log', 'info', 'warn', 'error'] as const;

type LogFn = (...args: unknown[]) => void;

type LoggerType = Record<(typeof LOG_METHODS)[number], LogFn> & { dev: LogFn };

const noop: LogFn = () => {};

function createLogger(id: string | undefined, level: number | keyof typeof LoggerLevel) {
  const cutoffLevel = typeof level === 'string' ? LoggerLevel[level] : level;
  const prefix = id ? `[${id}] ` : '';
  const logger = LOG_METHODS.reduce((logger, method) => {
    logger[method] =
      cutoffLevel <= LoggerLevel[method] ? console[method].bind(console, prefix) : noop;
    return logger;
  }, {} as LoggerType);
  logger.dev =
    cutoffLevel <= LoggerLevel.dev ?
      (...args: unknown[]) => {
        if (TeoCore.DEBUG) {
          console.info(`${prefix} >>>`, ...args);
        }
      }
    : noop;
  return logger;
}

class LoggerManager {
  #loggers: Record<string, LoggerType> = {};
  #level: number | keyof typeof LoggerLevel = LoggerLevel.all;
  constructor(level: number | keyof typeof LoggerLevel = LoggerLevel.all) {
    this.#level = level;
  }

  logger(id: string) {
    if (!this.#loggers[id]) {
      this.#loggers[id] = createLogger(id, this.#level);
    }
    return this.#loggers[id];
  }

  setLevel(level: number | keyof typeof LoggerLevel) {
    if (this.#level === level) return;
    this.#level = level;
    Object.entries(this.#loggers).forEach(([id, logger]) => {
      const newLevelLogger = createLogger(id, level);
      Object.assign(logger, newLevelLogger);
    });
  }
}

const loggerManager = new LoggerManager();

export function Logger(id: string) {
  return loggerManager.logger(id);
}

export function setLogLevel(level: keyof typeof LoggerLevel) {
  loggerManager.setLevel(level);
}

export const LOG = Logger('*');
